import * as Sentry from '@sentry/react';
import {
  buildGateDecisionPayload,
  gateDecisionKey,
  MFA_GATE_TRAIL_CATEGORY,
  type MfaGateDecision,
  type MfaGateDecisionInputs,
} from '@usacsmideo/types';

/**
 * The gate-decision trail — CU-86ajxvg7y.
 *
 * ── THE PROBLEM ───────────────────────────────────────────────────────────
 *
 * `useMfaRouting` recorded a non-`pass` decision as `Sentry.addBreadcrumb`.
 * ⚠️ BREADCRUMBS ONLY SURFACE ATTACHED TO A CAPTURED EVENT. If the gate decides
 * unexpectedly and nothing else throws, the inputs it decided on are gone.
 *
 * That already cost a debugging session: working out why an exception bypass was
 * refused offline meant replaying real Firestore values through `decideMfaGate`
 * locally, because telemetry held nothing. Post-cutover it becomes "a patient
 * says they were gated and we cannot tell why".
 *
 * ── ⚠️ OPTION 2 — TRANSITIONS, NOT RENDERS. THE VOLUME ARGUMENT ───────────
 *
 * Capturing every non-`pass` decision was rejected on a STRUCTURAL multiplier,
 * not a guess about traffic:
 *
 *  1. ⚠️ `useMfaRouting` IS CALLED TWICE PER GATED VIEW — `$patientId.tsx` (to
 *     gate the chart listener) and `MfaRouteGate` (to gate the render). Both run
 *     the same effect; both would emit. Every per-render figure starts at 2x,
 *     and the second call site exists for a real reason, so it is not removable.
 *  2. The effect re-runs on `enforced`, `enrollmentState`, `exception` and the
 *     refresh nonce — and ENROLLING CHANGES THOSE FIELDS, so acting on the gate
 *     makes the doc listener re-emit and both hooks re-decide.
 *  3. Gated patients RETRY. The whole population meets this gate at once, when
 *     the sweep revokes their sessions, and a patient who cannot immediately
 *     satisfy it reloads.
 *
 * ⚠️ THE POPULATION IS THE MULTIPLICAND AND IT IS GATED SIMULTANEOUSLY. These
 * events do not arrive spread over a month; they arrive in the hours after the
 * sweep. Per-render capture turns one cutover into (population x 2 x re-decides
 * x retries) events inside a single quota window.
 *
 * Transition capture bounds it to one event per session per DISTINCT decision —
 * which is also the only shape support can read. The tenth identical capture
 * answers no question the first did not.
 *
 * ⚠️ THE BREADCRUMB IS DELIBERATELY KEPT. It is free, and it still gives full
 * per-render ordering inside any event that IS captured. This adds the durable
 * record; it does not replace the trail.
 *
 * ── ⚠️ NOT THE SAME SIGNAL AS FACTOR-CHOICE TELEMETRY ─────────────────────
 *
 * This answers "why was THIS patient gated?" (support). `factorChoiceTelemetry`
 * answers "what did the POPULATION choose?" (the retirement decision). Separate
 * category, separate message prefix, so a population query never silently
 * includes diagnosis noise.
 */

/** ⚠️ Changing this breaks saved Sentry searches — update both. */
const MESSAGE_PREFIX = 'mfa gate decision';

/**
 * ⚠️ MODULE-LEVEL BECAUSE THE HOOK HAS TWO CALL SITES.
 *
 * Two `useMfaRouting` instances hold INDEPENDENT React state, so per-instance
 * memory would still emit twice and halve nothing. Shared module state is what
 * actually makes this one-per-transition.
 */
const seenDecisions = new Set<string>();

/** Test seam. ⚠️ Never called from app code. */
export function __resetGateDecisionTrailForTest(): void {
  seenDecisions.clear();
}

/**
 * Record a gate decision, ONCE per distinct (uid, decision, inputs).
 *
 * ⚠️ ONLY NON-`pass` DECISIONS REACH HERE — a passing gate is the steady state
 * and needs no record. Kept at the call site so this function stays a pure
 * emitter.
 *
 * ⚠️ NEVER THROWS. Telemetry must not break the gate it observes.
 */
export function recordGateDecision(
  uid: string | undefined,
  decision: MfaGateDecision,
  inputs: MfaGateDecisionInputs,
): void {
  try {
    // ⚠️ Without a uid we cannot deduplicate per patient, and an unbounded
    // stream is exactly what option 2 exists to avoid. A gated render always
    // has a patient doc, so this is a guard rather than a live path.
    if (!uid) return;

    const key = gateDecisionKey(uid, decision, inputs);
    if (seenDecisions.has(key)) return;
    seenDecisions.add(key);

    Sentry.captureMessage(`${MESSAGE_PREFIX}: ${decision}`, {
      // ⚠️ `warning`, not `error`. Post-cutover a gated patient is EXPECTED
      // behaviour, not a fault — alarming on it would train the team to ignore
      // the one signal that says the gate is misfiring.
      level: 'warning',
      tags: {
        feature: 'mfa',
        signal: MFA_GATE_TRAIL_CATEGORY,
        decision,
      },
      extra: buildGateDecisionPayload(decision, inputs),
      /**
       * ⚠️ GROUPED BY KIND, NOT BY STACKTRACE — see the long note in
       * `factorChoiceTelemetry.ts`. Without this, Sentry groups on frames: the
       * same three logical events produced SIX issues on 2026-09-01, and every
       * new bundle would mint fresh ones (mobile's A4 was titled
       * `recordGateDecision`, from minified frames).
       *
       * ⚠️ THE THIRD OF THE THREE GROUPS, and deliberately separate from the
       * factor-choice pair: this trail answers "why was THIS patient gated?",
       * that funnel answers "what did the POPULATION choose?". The two are kept
       * apart everywhere else (different category, different message prefix);
       * merging them here would undo that in the one place it is load-bearing.
       *
       * ⚠️ `decision` STAYS OUT OF THE KEY even though it is in the message.
       * It is already a TAG, so it groups in Discover without splitting the
       * issue — and including it would make the archive set grow with every new
       * decision value, which is exactly the fragility this removes.
       */
      fingerprint: ['mfa-telemetry', 'gate-decision'],
    });
  } catch {
    // Swallowed on purpose. See the contract above.
  }
}
